import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getLabBySlug } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Clock, ArrowLeft, CheckCircle, Play,
  Loader2, Code, Lightbulb, Award, Target,
  BookOpen, Layers, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '@/components/SEOHead';

const LabDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [lab, setLab] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLab = async () => {
      try {
        const data = await getLabBySlug(slug);
        setLab(data);
      } catch (error) {
        console.error('Failed to fetch lab:', error);
        if (error.response?.status === 404) {
          navigate('/labs');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchLab();
  }, [slug, navigate]);

  const handleStartLab = () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/labs/${slug}/practice`)}`);
      return;
    }
    navigate(`/labs/${slug}/practice`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!lab) return null;

  const steps = lab.steps || [];
  const difficultyColor = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800',
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={lab.title}
        description={lab.short_description}
        url={`/labs/${slug}`}
      />

      {/* Hero section */}
      <div className="bg-slate-900 text-white">
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <Link
            to="/labs"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Labs
          </Link>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge className={difficultyColor[lab.difficulty] || 'bg-slate-100 text-slate-800'}>
                  {lab.difficulty}
                </Badge>
                <Badge variant="outline" className="border-slate-600 text-slate-300">
                  {lab.technology || lab.category}
                </Badge>
                {lab.is_interactive && (
                  <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    <Code className="w-3 h-3 mr-1" /> Interactive
                  </Badge>
                )}
              </div>

              <h1 className="text-3xl font-bold tracking-tight mb-3">
                {lab.title}
              </h1>
              <p className="text-lg text-slate-300 mb-6 max-w-2xl">
                {lab.description || lab.short_description}
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{lab.estimated_time_minutes || 60} min</span>
                </div>
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  <span>{steps.length} Steps</span>
                </div>
                {lab.completions_count > 0 && (
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    <span>{lab.completions_count} Completions</span>
                  </div>
                )}
              </div>
            </div>

            <div className="md:text-right shrink-0">
              <Button
                size="lg"
                onClick={handleStartLab}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
              >
                <Play className="w-5 h-5 mr-2" />
                {lab.is_completed ? 'Redo Lab' : 'Start Lab'}
              </Button>
              {!isAuthenticated && (
                <p className="text-xs text-slate-500 mt-2">Login required to start</p>
              )}
              {lab.is_completed && (
                <div className="mt-3">
                  <Badge className="bg-green-500 text-white">
                    <CheckCircle className="w-3 h-3 mr-1" /> Completed
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-10 max-w-5xl">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main content — 2 columns */}
          <div className="md:col-span-2 space-y-8">
            {/* Steps overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Lab Steps
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {steps.map((step, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center shrink-0 text-sm">
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium">{step.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {step.description}
                      </p>
                      {step.hints && step.hints.length > 0 && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-yellow-600">
                          <Lightbulb className="w-3 h-3" />
                          <span>{step.hints.length} hint{step.hints.length > 1 ? 's' : ''} available</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar — 1 column */}
          <div className="space-y-6">
            {/* Prerequisites */}
            {lab.prerequisites && lab.prerequisites.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Prerequisites
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {lab.prerequisites.map((prereq, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                        {prereq}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Skills gained */}
            {lab.skills_gained && lab.skills_gained.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Skills You'll Gain
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {lab.skills_gained.map((skill, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Start CTA (sidebar) */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6 text-center">
                <Code className="w-10 h-10 text-primary mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Ready to get started?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Jump into the interactive lab environment and start coding.
                </p>
                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={handleStartLab}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Launch Lab
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabDetailPage;
